import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Heading,
  Row,
  Column,
  Box,
  Alert,
  BodyText,
} from '@chargify/custom-ui-components';
import { Address, AddressFields } from '@chargify/address';
import { useDeviceInfo } from '@chargify/device-provider';

import { CreditCard } from '../CreditCard';
import { usePaymentProfileTranslations } from '../../hooks';
import { PaymentsFormConfig, CreditCardFields } from '../../types';
import { PaymentProfileSkeleton } from '../../components';

import { FORM_ELEMENT_ID, US_FORMAT_ADDRESS_FORM } from './constants';

type PaymentProfileProps = {
  /**
   * Payment method type used to render specific form
   */
  paymentMethodType?: 'creditCard' | 'bankAccount';
  /**
   * Use 3DS for collecting payment method
   */
  useThreeDSecure?: boolean;
  /**
   * Form HTML element identifer
   */
  formElementId?: string;
  /**
   * Payment profile required fields
   */
  requiredFields?: string[];
  /**
   * Payments form configurartion
   */
  config: PaymentsFormConfig;
  /**
   * Update form field event handler
   */
  onUpdateField: (
    fieldName: keyof AddressFields | keyof CreditCardFields,
    form: 'billingAddress' | 'paymentMethod',
    value: string | number
  ) => void;
  /**
   * Validate form field event handler
   */
  onValidateField: (
    fieldName: keyof AddressFields | keyof CreditCardFields,
    form: 'billingAddress' | 'paymentMethod',
    value: string | number
  ) => void;
  /**
   * Billing address fields
   */
  billingAddress: {
    values: AddressFields;
    errors: { [key in keyof AddressFields]?: 'required' | 'invalid' };
  };
  /**
   * Payment method fields
   */
  paymentMethod: {
    values: CreditCardFields;
    hostedFieldsErrors: string[];
    errors: { [key in keyof CreditCardFields]?: 'required' | 'invalid' };
  };
  /**
   * Element to which the dropdwon should be closed on outside click
   */
  targetElementRef?: RefObject<HTMLElement>;
};

const PaymentProfile = ({
  paymentMethodType = 'creditCard',
  requiredFields = [],
  useThreeDSecure = false,
  formElementId = FORM_ELEMENT_ID,
  onUpdateField,
  onValidateField,
  billingAddress,
  paymentMethod,
  config,
  targetElementRef,
}: PaymentProfileProps) => {
  const { device } = useDeviceInfo();
  const { t } = useTranslation(['payment-profile']);

  const { creditCardTranslations } = usePaymentProfileTranslations();

  const {
    values: { address, address2, city, state, country, zip },
  } = billingAddress;

  const { hostedFieldsErrors } = paymentMethod;
  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);
  return (
    <div id={formElementId}>
      {hostedFieldsErrors.length > 0 ? (
        <Box marginBottom={8}>
          <Alert color={'danger'} title={t('failed_saving_payment_method')}>
            <BodyText variant={'body2'} color={'danger.dark'}>
              {t('did_you_fill_all_fields')}
            </BodyText>
          </Alert>
        </Box>
      ) : null}
      {paymentMethodType === 'creditCard' && (
        <Row rowGap={['5', '5', '0']}>
          <Column span={[24, 7]}>
            <Heading variant="h4">
              {t('credit_card_details_header', { ns: 'payment-profile' })}
            </Heading>
          </Column>
          <Column span={[24, 17]}>
            <CreditCard
              config={config}
              translations={creditCardTranslations}
              isOnMobileDevice={isOnMobileDevice}
              values={paymentMethod.values}
              errors={paymentMethod.errors}
              hostedFieldsErrors={hostedFieldsErrors}
              creditCardSize={device === 'mobile' ? 'small' : 'regular'}
              requiredFields={requiredFields}
              useThreeDSecure={useThreeDSecure}
              onValidateField={(fieldName, value) =>
                onValidateField(fieldName, 'paymentMethod', value)
              }
              onChange={(fieldName, value) =>
                onUpdateField(fieldName, 'paymentMethod', value)
              }
            />
          </Column>
        </Row>
      )}
      <Box marginTop={7}>
        <Row rowGap={['5', '5', '0']}>
          <Column span={[24, 7]}>
            <Heading variant="h4">
              {t('billing_address_header', { ns: 'payment-profile' })}
            </Heading>
          </Column>
          <Column span={[24, 17]}>
            <Address
              requiredFields={requiredFields}
              values={billingAddress.values}
              errors={billingAddress.errors}
              format={
                US_FORMAT_ADDRESS_FORM.includes(country)
                  ? 'us'
                  : 'international'
              }
              onValidateField={(fieldName, value) =>
                onValidateField(fieldName, 'billingAddress', value)
              }
              onChange={(fieldName, value) =>
                onUpdateField(fieldName, 'billingAddress', value)
              }
              targetElementRef={targetElementRef}
            />
          </Column>
        </Row>
      </Box>
      <input type="hidden" data-chargify="address" value={address} />
      <input type="hidden" data-chargify="address2" value={address2} />
      <input type="hidden" data-chargify="city" value={city} />
      <input type="hidden" data-chargify="state" value={state} />
      <input type="hidden" data-chargify="country" value={country} />
      <input type="hidden" data-chargify="zip" value={zip} />
    </div>
  );
};

PaymentProfile.LoadingSkeleton = PaymentProfileSkeleton;

export default PaymentProfile;
